<template>
  <div>
    <app-cells position="between">
      <app-button v-if="show_add_btn" @click.native="onServiceCreate">Add service</app-button>
      <app-cells v-if="groupDetail.type?.code !== 'join'" :indent="false">
        Bottled water for cooking: <app-switcher v-model="groupDetail.water_for_cooking" @change="onChangeWaterSwitcher" />
      </app-cells>
    </app-cells>
    <div v-for="service_item of service_items" :key="service_item.code" class="service-block">
      <app-cells v-if="service_item.name">
        <h2 class="title">{{ service_item.name }}</h2>
      </app-cells>
      <v-client-table
        :data="service_item.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #count="{ row }">{{ row.index }}</template>
        <template #h__clients><div class="table-default__left">Clients</div></template>
        <template #clients="{ row }">
          <app-cells :indent="false">
            <div
              v-for="(client, index) of row.clients"
              :key="client.id"
              class="table-default__left table-default__left--photo"
            >
          <span
            :class="`fflag ff-md fflag-${client.client.citizenship ? client.client.citizenship.code : 'empty'}`"
            v-tooltip="{ content: client.client.citizenship ? client.client.citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
          ></span>
              <img v-if="client.client.gender?.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
              <img v-if="client.client.gender?.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
              <img v-if="!client.client.gender" src="@/assets/img/gender-plug-icon.svg" alt="">
              <router-link
                v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_operations_manager || is_operations_supervisor"
                :to="{ name: 'clients-detail', params: { id: client.client.id } }"
                class="table-default__link-border"
              >
                {{ client.client.full_name }}
              </router-link>
              <span v-else>{{ client.client.full_name }}</span>
              <span v-if="client.age">({{ client.age }} y.o.)</span>
              {{ index !== row.clients.length - 1 ? '|' : '' }}
            </div>
          </app-cells>
        </template>
        <template #ready_to_share="{ row }">
        <span class="status-tag" :style="{ 'backgroundColor': row.ready_to_share ? '#77b017' : '#bfbfbf' }">
          {{ row.ready_to_share ? 'Yes' : 'No' }}
        </span>
        </template>
        <template #created_by="{ row }">
          <div v-if="row.created_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                v-tooltip="{ content: `${row.created_by.full_name}<br>${dateTimeFormat(row.created_at)}`, placement: 'top-start', offset: 10, html: true }"
              >
            </div>
          </div>
          <template v-else>-</template>
        </template>
        <template #updated_by="{ row }">
          <div v-if="row.updated_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.updated_by.crop.crop_url)" alt=""
                v-tooltip="{ content: tooltipContent(row), placement: 'top-start', offset: 10, html: true }"
              >
            </div>
          </div>
          <template v-else>-</template>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onServiceEdit(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
    </div>
    <modal
        name="service-form"
        :width="450"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('service-form')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ is_create ? 'Adding' : 'Editing' }} additional service</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Service" required>
              <app-select
                @input="onChangeService"
                v-model="form.service"
                :options="service_options"
                :clearable="false"
                :display-field="option => `${option.name} (pax limit: ${servicePaxLimit(option)})`"
                placeholder="Service"
                :error="$v.form.service.$error"
              />
              <template #error>
                <div v-if="$v.form.service.$dirty && !$v.form.service.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Clients" required>
              <app-select
                v-model="form.clients"
                :options="clients_options"
                :selectable="onCalcPaxLimit"
                :clearable="false"
                :readonly="!form.service"
                :get-option-label="item => item.client.full_name"
                :display-field="option => option.client.full_name"
                multiple
                placeholder="Clients"
                :error="$v.form.clients.$error"
              />
              <template #error>
                <div v-if="$v.form.clients.$dirty && !$v.form.clients.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group v-if="formServicePaxLimit > 1" label="Ready to share">
              <app-switcher
                  v-model="form.ready_to_share"
                  :readonly="formServicePaxLimit === form.clients.length"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">
                {{ is_create ? 'Add' : 'Save' }} service
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
        name="delete-modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete {{ service_to_delete ? 'product' : 'transfer' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteService" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import {
  deleteClimbingOrderService,
  getClimbingAdditionalServiceDict,
  getClimbingGroupClientOptions,
  getClimbingOrderService,
  patchClimbingOrderService,
  patchClimbingsGroups,
  postClimbingOrderService,
} from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'ClimbingGroupsDetailServices',
  props: {
    groupDetail: {
      type: Object
    }
  },
  data() {
    return {
      is_loaded: false,
      service_items: [],
      table: {
        items: [],
        columns: ['count', 'ready_to_share', 'clients', 'created_by', 'updated_by'],
        options: {
          headings: {
            'count': '#',
            'ready_to_share': 'Share',
            'clients': 'Clients',
            'created_by': 'Created by',
            'updated_by': 'Updated by',
            'controls': ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      form: {
        clients: [],
        ready_to_share: false
      },
      service_options: [],
      clients_options: [],
      is_create: false,
      service_to_delete: null
    }
  },
  validations: {
    form: {
      service: { required },
      clients: { required },
    },
  },
  computed: {
    formServicePaxLimit() {
      return this.servicePaxLimit(this.form.service)
    },
    show_controls() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    show_add_btn() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_operations_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_manager')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    },
  },
  watch: {
    'form.clients'() {
      if (this.formServicePaxLimit > 1 && this.formServicePaxLimit === this.form.clients.length) {
        this.form.ready_to_share = false
      }
    }
  },
  created() {
    this.fetchItems()
    getClimbingAdditionalServiceDict({tents: false, available_routes: this.groupDetail.route.id, available_types: this.groupDetail.type.code})
      .then(res => { this.service_options = res.data })
    getClimbingGroupClientOptions(this.$route.params.id, { only_visible: this.is_sales_manager })
      .then(res => { this.clients_options = res.data })
    if (this.show_controls) this.table.columns.push('controls')
  },
  methods: {
    onChangeWaterSwitcher() {
      patchClimbingsGroups(this.$route.params.id, { water_for_cooking: this.groupDetail.water_for_cooking })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Changes saved',
          })
        })
    },
    onDeleteService() {
      this.$refs.delete.preload = true
      deleteClimbingOrderService(this.$route.params.id, this.service_to_delete)
        .finally(() => { this.$refs.delete.preload = false })
        .then(() => {
          this.fetchItems()
          this.$emit('refreshTabCounts')
          this.$modal.hide('delete-modal')
        })
    },
    onShowDeleteModal(id) {
      if (id) this.service_to_delete = id
      this.$modal.show('delete-modal')
    },
    onServiceCreate() {
      this.is_create = true
      this.form = {
        clients: [],
        ready_to_share: false
      }
      this.$v.form.$reset()
      this.$modal.show('service-form')
    },
    onServiceEdit(row) {
      this.form = {
        id: row.id,
        service: row.service,
        clients: row.clients,
        ready_to_share: row.ready_to_share,
      }
      this.is_create = false
      this.$modal.show('service-form')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    fetchItems() {
      getClimbingOrderService(this.$route.params.id)
        .then(response => {
          this.service_items = response.data.length ? response.data : [{name: '', items: []}]
        })
    },
    onChangeService() {
      this.form.clients = []
      if (this.formServicePaxLimit <= 1) this.form.ready_to_share = false
    },
    servicePaxLimit(service) {
      if (!service) return 0
      return service.code === 'porter' ? 3 : service.pax_limit
    },
    onCalcPaxLimit() {
      if (this.form.service) return this.form.clients.length < this.formServicePaxLimit
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.is_create) {
        postClimbingOrderService(this.$route.params.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$emit('refreshTabCounts')
            this.$notify({
              type: 'success',
              title: 'Service added',
            })
            this.$modal.hide('service-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        patchClimbingOrderService(this.$route.params.id, this.form.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$emit('refreshTabCounts')
            this.$notify({
              type: 'success',
              title: 'Service saved',
            })
            this.$modal.hide('service-form')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      if (typeof normalizedForm.service !== 'string') normalizedForm.service = normalizedForm.service.code
      if (normalizedForm.clients.every(item => typeof item === 'object')) {
        normalizedForm.clients = normalizedForm.clients.map(item => item.id)
      }
      delete normalizedForm.id
      return normalizedForm
    },
    tooltipContent(data) {
      return `${data.updated_by.full_name}<br>${this.dateTimeFormat(data.updated_at)}`
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  }
}
</script>

<style scoped lang="sass">
.service-block
  margin-bottom: 30px

  &:last-child
    margin-bottom: 0
</style>
